import React from 'react';
import { addError, addAction } from '@shared/utils/logger';
import { isChunkLoadError } from '@shared/utils/isChunkLoadError';
import { isPhotoDownloadPath } from '@shared/utils/photoDownloadPath';

// A PageAction attribute is sent verbatim (NR only strips queries from URLs it records itself), so the
// photo-download page's signed zip URL must be dropped here.
const reportableUrl = ({ href, origin, pathname }: Location): string => (isPhotoDownloadPath(pathname) ? `${origin}${pathname}` : href);

type ErrorBoundaryState = Readonly<{
  hasError: boolean;
  error: Error | null;
  /**
   * Two-phase fallback commit. When the error is caught DURING hydrate(),
   * React 17 positionally adopts existing SSR DOM nodes for the fallback and
   * only patches TEXT mismatches — attribute mismatches (our inline `style`
   * props) are silently ignored, so the error page rendered unstyled
   * (left-aligned, unthemed). Rendering `null` for one commit first deletes
   * the adopted SSR region; `componentDidCatch` (post-commit) then flips this
   * flag so the fallback mounts into an EMPTY container with fresh nodes and
   * intact attributes. Pinned by ErrorBoundary.spec.tsx's hydration case.
   */
  canCommitFallback: boolean;
}>;

export type ErrorBoundaryProps = Readonly<{
  children?: React.ReactNode;
}>;

/**
 * Top-level error boundary for the entire application.
 *
 * Catches render errors (including failed lazy-loaded chunks) and:
 * 1. Reports to New Relic via noticeError + a structured PageAction
 * 2. Shows a fallback UI with a reload button
 *
 * Does NOT catch: event handlers, async code, SSR errors, or errors
 * thrown in the boundary itself.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, canCommitFallback: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // canCommitFallback stays false: render() emits a null frame first (see the
    // state field's JSDoc — required for a styled fallback on hydration errors).
    return { hasError: true, error, canCommitFallback: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const chunkError = isChunkLoadError(error);

    addError(error, {
      componentStack: errorInfo.componentStack,
      boundary: 'AppErrorBoundary',
      isChunkLoadError: chunkError,
    });

    // Structured PageAction for faceted NR queries
    addAction('AppErrorBoundaryCatch', {
      errorName: error.name,
      errorMessage: (error.message ?? '').slice(0, 200),
      isChunkLoadError: chunkError,
      url: typeof window !== 'undefined' ? reportableUrl(window.location) : undefined,
    });

    // Phase 2 of the fallback commit: this runs AFTER the null frame committed
    // (and cleared any hydration-adopted SSR DOM), so the fallback mounts fresh
    // with its inline styles intact. One imperceptible blank frame on an error page.
    this.setState({ canCommitFallback: true });
  }

  private handleReload = () => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    if (isChunkLoadError(this.state.error)) {
      // Bump retry param to cache-bust all SSR-emitted resources
      const current = Number(url.searchParams.get('retry')) || 0;
      url.searchParams.set('retry', String(current + 1));
    }

    window.location.assign(url.toString());
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Null frame between catch and fallback — see canCommitFallback's JSDoc.
    if (!this.state.canCommitFallback) {
      return null;
    }

    const chunkError = isChunkLoadError(this.state.error);

    // Fallback UI uses inline styles — this boundary wraps the entire app
    // including all providers (joykit, styled-components, i18n), so we
    // cannot depend on any of them here.
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <svg
            viewBox="0 0 33 20"
            style={styles.logo}
            aria-label="Joy"
          >
            <path
              fill="#171717"
              d="M28.075 7.596c-1.242-5.167-4.856-2.73-5.484-1.926a.065.065 0 00.06.104c2.693-.386 3.278 8.915 2.187 12.26-.02.064.056.114.106.069.886-.797 4.568-4.536 3.131-10.507zM32.41 6.891c.183-.861.198-2.199-1.054-2.6-.895-.287-2.575.682-1.58 2.318.456.749 1.314 2.64.644 4.665-.024.074.076.123.117.057.56-.905 1.493-2.65 1.873-4.44zM23.951 18.802c.07-.02.117.07.06.115-.958.752-1.835 1.096-2.891 1.048-2.352-.108-2.461-1.982-1.768-2.841.34-.42 1.105-.896 1.587-.877a.064.064 0 01.055.088c-.443 1.08.474 3.209 2.957 2.467z"
            />
            <path
              fill="#171717"
              d="M18.453 4.412c-1.802 0-3.393.702-4.657 1.756-1.311 1.123-2.34 3.065-2.388 5.008 0 2.644 1.896 4.376 4.658 4.376a6.944 6.944 0 004.563-1.732c1.334-1.146 2.41-3.136 2.41-5.078 0-2.621-1.918-4.33-4.586-4.33zm.723 5.007c-.16.616-1.146 4.344-2.603 5.103-2.101 1.095-1.536-2.96-1.251-4 .168-.614 1.143-4.385 2.603-5.102 2.084-1.026 1.439 3.28 1.251 3.999z"
            />
            <path
              fill="#171717"
              d="M1.259 14.415c.045.046.124.006.11-.058-.152-.693.585-1.735 1.976-1.631.869.065 1.31-1.315.593-2.042-.887-.9-2.212-.546-2.875.391-1.028 1.454-.456 2.666.196 3.34zM3.946 15.414c-.08.01-.074.127.006.13 1.913.09 5.805-.755 6.936-5.433.34-1.41 1.283-5.3 1.543-6.38.54-2.246.918-3.054 1.244-3.326a.064.064 0 00-.015-.109C12.98-.01 9.786-.868 8.896 3.066c-.12.53-1.311 5.728-1.45 6.316-1.105 4.66-2.165 5.871-3.5 6.032z"
            />
          </svg>

          <h1 style={styles.heading}>{chunkError ? "This page couldn't load completely" : "Hmm\u2026 we're having trouble with that."}</h1>

          <p style={styles.subtitle}>
            {chunkError ? 'Some resources failed to load. This is usually temporary \u2014 a reload should fix it.' : "This page can't be loaded at the moment. Please try again."}
          </p>

          <button
            type="button"
            onClick={this.handleReload}
            style={styles.button}
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
}

// Inline styles — must not depend on any external CSS, styled-components, or joykit.
// Aligned with Joy design language: white bg, Inter font, brand blue (#4951EF).
const styles: Record<string, React.CSSProperties> = {
  container: {
    boxSizing: 'border-box',
    margin: 0,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    background: '#fff',
    color: '#171717',
    textAlign: 'center',
    padding: 24,
  },
  content: {
    maxWidth: 480,
  },
  logo: {
    width: 48,
    height: 'auto',
    marginBottom: 24,
  },
  heading: {
    fontSize: 24,
    fontWeight: 500,
    letterSpacing: '-0.02em',
    margin: '0 0 12px',
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 1.6,
    color: '#6B7280',
    margin: '0 0 24px',
  },
  button: {
    display: 'inline-block',
    padding: '12px 24px',
    background: '#4951EF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
